import { useAppSelector } from "@app/hooks";
import { LBtn, LFlex, LInputRow, LTitle } from "@components";
import { EditPrimaryContactModal } from "@components/EditPrimaryContactModal";
import { LDivider } from "@components/LDivider";
import { DropdownMenuOption, LDropdownMenu } from "@components/LDropdownMenu";
import { InputFieldProps } from "@components/LInputField";
import { LTooltip } from "@components/LTooltip";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getEnumValueByKey } from "@helpers/enumHelper";
import { DefaultErrorMessages } from "@helpers/formValidationHelper";
import { NavItems } from "@helpers/navigation";
import {
    apiOrganizationStatus,
    apiOrganizationToOrganization,
    getReinstateRevokeToolTipText,
    Organization,
    OrganizationStatus
} from "@helpers/organizationHelper";
import { yupResolver } from "@hookform/resolvers/yup";
import { useApi } from "@services/useApi";
import { Purchase } from "@store/generalTypes";
import { updateOrganization } from "@store/organizationSlice";
import { Colors } from "@styles/colors";
import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import { Control, FormProvider, SubmitHandler, useForm, useWatch } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { SchemaOf, object, number } from "yup";

const SkeletonWrapper = ({
    children,
    className = "",
    loading
}: {
    children: React.ReactNode;
    className?: string;
    loading: boolean;
}): JSX.Element => {
    return (
        <>
            {loading && <Skeleton className={className} />}
            {!loading && children}
        </>
    );
};

const OrganizationRequestAssessmentQuantity = ({
    control,
    loading
}: {
    control: Control<AssessmentQuantityForm>;
    loading: boolean;
}): JSX.Element => {
    const assessmentQuantity = Number(useWatch({ control, name: "assessmentQuantity", defaultValue: 0 }));
    const unitPrice = Number(useWatch({ control, name: "unitPrice", defaultValue: 0 }));
    const discount = Number(useWatch({ control, name: "discount", defaultValue: 0 }));

    const subTotal = assessmentQuantity * unitPrice;
    const discountTotal = (subTotal * discount) / 100;
    const total = subTotal - discountTotal;

    const inputs = [
        {
            label: "Assessment Quantity",
            labelColor: Colors.darkGray2,
            type: "number",
            isNumber: true,
            inputName: "assessmentQuantity",
            placeholder: "Enter quantity",
            defaultValue: ""
        } as InputFieldProps,
        {
            label: "Unit Price $",
            labelColor: Colors.darkGray2,
            type: "number",
            isNumber: true,
            inputName: "unitPrice",
            placeholder: "Enter unit price",
            defaultValue: ""
        } as InputFieldProps,
        {
            label: "Discount % (optional)",
            labelColor: Colors.darkGray2,
            type: "text",
            isNumber: true,
            inputName: "discount",
            placeholder: "Enter discount %",
            defaultValue: "",
            maxLength: 2
        } as InputFieldProps
    ];

    return (
        <>
            <SkeletonWrapper loading={loading}>
                <LInputRow inputs={inputs} />
            </SkeletonWrapper>
            <div
                className="mt-3 p-5 has-background-light-blue has-text-ldark-gray is-borders-rounded"
                style={{ maxWidth: "60%" }}
            >
                <SkeletonWrapper loading={loading}>
                    <LFlex row alignCenter className="is-justify-content-space-between">
                        <p>Subtotal</p>
                        <p>${subTotal?.toFixed(2)}</p>
                    </LFlex>
                </SkeletonWrapper>
                <SkeletonWrapper className="mt-3" loading={loading}>
                    <LFlex row alignCenter className="is-justify-content-space-between mt-3">
                        <p>Discount</p>
                        <p>${discountTotal?.toFixed(2)}</p>
                    </LFlex>
                </SkeletonWrapper>
                <LDivider />
                <SkeletonWrapper loading={loading}>
                    <LFlex row alignCenter className="is-justify-content-space-between">
                        <p className="has-text-weight-bold is-size-5">Total</p>
                        <p className="has-text-weight-bold is-size-5">${total?.toFixed(2)}</p>
                    </LFlex>
                </SkeletonWrapper>
            </div>
        </>
    );
};

const Row = ({
    children,
    marginBottom,
    marginTop
}: {
    children: React.ReactNode;
    marginBottom?: number;
    marginTop?: number;
}): JSX.Element => {
    return (
        <div
            className={classNames(
                "is-flex",
                "is-flex-direction-row",
                "is-justify-content-space-between",
                "is-align-items-center",
                marginBottom && `mb-${marginBottom}`,
                marginTop && `mt-${marginTop}`
            )}
        >
            {children}
        </div>
    );
};

const Tile = ({
    title = "-",
    content = "-",
    isBoldContent,
    containerClasses = "-"
}: {
    title: string | undefined;
    content: string | undefined;
    isBoldContent?: boolean;
    containerClasses?: string;
}): JSX.Element => {
    return (
        <div className={classNames(containerClasses, "tile", "is-flex", "is-flex-direction-column")}>
            <div className="label is-small has-text-grey-light mb-0">{title}</div>
            <div className={isBoldContent ? "has-text-weight-bold" : ""}>
                <div>{content || "-"}</div>
            </div>
        </div>
    );
};

const MultiLineTile = ({
    title = "-",
    items = [],
    containerClasses = "-",
    loading = false
}: {
    title: string | undefined;
    items: MultiLineTileItem[];
    containerClasses?: string;
    loading?: boolean;
}): JSX.Element => {
    return (
        <div className={classNames(containerClasses, "tile", "is-flex", "is-flex-direction-column")}>
            <SkeletonWrapper loading={loading}>
                <div className="label is-small has-text-grey-light mb-0">{title}</div>
            </SkeletonWrapper>
            {items.map(({ value, tooltipText }, i) => (
                <SkeletonWrapper key={`skeleton-${i}`} loading={loading}>
                    <div key={`${title}-${i}`} title={tooltipText} className={"has-text-weight-bold"}>
                        <div>{value ?? "-"}</div>
                    </div>
                </SkeletonWrapper>
            ))}
        </div>
    );
};

const formSchema: SchemaOf<AssessmentQuantityForm> = object({
    assessmentQuantity: number()
        .required(DefaultErrorMessages.required)
        .typeError(DefaultErrorMessages.required)
        .min(0),
    unitPrice: number().required(DefaultErrorMessages.required).typeError(DefaultErrorMessages.required).min(0),
    discount: number()
        .typeError(DefaultErrorMessages.numeric())
        .moreThan(0)
        .lessThan(100)
        .transform((v, o) => (Number(o) ? v : undefined))
});

const SuperOrganizationDetails = (): JSX.Element => {
    const { id } = useParams();
    const navigate = useNavigate();

    const userOrganization = useAppSelector(({ organization }) => organization);
    const dispatch = useDispatch();

    const { getOrganization, updateOrganizationStatus, deleteOrganization, createPurchase, getPurchases, loading } =
        useApi();
    const [organization, setOrganization] = useState<Organization | null>(null);
    const [purchaseHistory, setPurchaseHistory] = useState(Array<Purchase>());
    const [showAssessmentQuantityForm, setShowAssessmentQuantityForm] = useState(false);
    const [isPrimaryContactModalOpen, setIsPrimaryContactModalOpen] = useState(false);

    const isUserOrganization = userOrganization.id === organization?.id;

    const isActiveOrganization = !!(organization?.status === OrganizationStatus.active.toLocaleLowerCase());

    const isRevokedAccessOrganization =
        (organization?.status as string) === apiOrganizationStatus[OrganizationStatus.accessRevoked];

    const validateMethods = useForm<AssessmentQuantityForm>({
        resolver: yupResolver(formSchema)
    });

    const formSubmitHandler: SubmitHandler<AssessmentQuantityForm> = async (data: AssessmentQuantityForm) => {
        if (!organization) {
            return;
        }

        await createPurchase(organization.id!, data.assessmentQuantity, true, data.unitPrice, data.discount);
        await handleGetOrganization(organization.id!);
    };

    const handleAddAssessments = async (e: React.MouseEvent): Promise<void> => {
        await validateMethods.handleSubmit(formSubmitHandler)(e);
        setShowAssessmentQuantityForm(false);
        validateMethods.reset();
    };

    const handleGrantAccess = async (e: React.MouseEvent): Promise<void> => {
        if (!showAssessmentQuantityForm) {
            setShowAssessmentQuantityForm(true);
        } else {
            await validateMethods.handleSubmit(formSubmitHandler)(e);

            navigate(NavItems.SuperAdmin.Organizations.path);
        }
    };

    const handleCancel = (): void => {
        setShowAssessmentQuantityForm(false);
    };

    const handleGetOrganization = async (organizationId: number): Promise<void> => {
        try {
            const organizationApi = await getOrganization(organizationId);
            const paginatedPurchasesApi = await getPurchases(organizationId, 1, 10);
            const _organization = apiOrganizationToOrganization(organizationApi);

            if (isUserOrganization) {
                dispatch(updateOrganization(_organization));
            }

            setOrganization(_organization);
            setPurchaseHistory(paginatedPurchasesApi.items);
        } catch (err) {
            console.error(err);
            navigate(NavItems.SuperAdmin.Organizations.path);
        }
    };

    const handleEditPrimaryContactClick = (): void => {
        setIsPrimaryContactModalOpen((isOpen) => !isOpen);
    };

    const handleSubmitEditPrimaryContact = (): void => {
        void handleGetOrganization(organization!.id!);
    };

    const handleDeleteOrganization = async (): Promise<void> => {
        try {
            await deleteOrganization(organization!.id!);
        } finally {
            navigate(NavItems.SuperAdmin.Organizations.path);
        }
    };

    const handleUpdateOrganizationStatus = async (
        status: OrganizationStatus.active | OrganizationStatus.accessRevoked
    ): Promise<void> => {
        try {
            await updateOrganizationStatus(organization!.id!, status);
        } finally {
            await handleGetOrganization(organization!.id!);
        }
    };

    const actionOptions = useMemo(
        () =>
            [
                {
                    optionName: isRevokedAccessOrganization ? "Reinstate" : "Revoke" + " Access",
                    disabled: loading,
                    children: (
                        <div className="columns m-2 is-align-items-center is-justify-content-space-between">
                            <LBtn
                                classes={classNames(
                                    "has-text-weight-bold",
                                    isRevokedAccessOrganization ? "has-text-grey-dark" : "has-text-danger"
                                )}
                                variation="unstyled"
                                noSpacing
                                loading={loading}
                                disabled={loading}
                                onClick={async () =>
                                    await handleUpdateOrganizationStatus(
                                        isRevokedAccessOrganization
                                            ? OrganizationStatus.active
                                            : OrganizationStatus.accessRevoked
                                    )
                                }
                            >
                                {isRevokedAccessOrganization ? "Reinstate" : "Revoke" + " Access"}
                            </LBtn>
                            <LTooltip
                                text={organization?.status ? getReinstateRevokeToolTipText(organization.status) : ""}
                            />
                        </div>
                    )
                },
                {
                    optionName: "Delete",
                    disabled: loading,
                    children: (
                        <div className="columns m-2 is-align-items-center is-justify-content-space-between">
                            <LBtn
                                classes="has-text-danger has-text-weight-bold"
                                variation="unstyled"
                                noSpacing
                                loading={loading}
                                disabled={loading}
                                onClick={handleDeleteOrganization}
                            >
                                Delete Organization
                            </LBtn>
                            <LTooltip text="By deleting the organization you are permanently removing them from the database. This action cannot be undone." />
                        </div>
                    )
                }
            ] as DropdownMenuOption[],
        [loading, organization]
    );

    useEffect(() => {
        void handleGetOrganization(Number(id));
    }, [id]);

    return (
        <div>
            <FormProvider {...validateMethods}>
                <SkeletonWrapper className="mb-5" loading={loading}>
                    <Row>
                        <LTitle size="4">Organization Details</LTitle>
                        <Row>
                            <LTitle
                                className="mb-0"
                                size="6"
                                color={
                                    [
                                        apiOrganizationStatus[OrganizationStatus.accessRevoked],
                                        apiOrganizationStatus[OrganizationStatus.purchaseIncomplete]
                                    ].includes(organization?.status as any)
                                        ? "has-text-danger"
                                        : undefined
                                }
                            >
                                {organization ? getEnumValueByKey(OrganizationStatus, organization.status) : ""}
                            </LTitle>
                            <LDropdownMenu
                                id="menu_organizationActions"
                                options={actionOptions}
                                isRightAligned
                                type="tableRowMenu"
                                hideErrorLabel
                                theme="light"
                                iconSize="medium"
                            />
                        </Row>
                    </Row>
                </SkeletonWrapper>
                <form onSubmit={validateMethods.handleSubmit(formSubmitHandler)}>
                    <SkeletonWrapper className="mb-5" loading={loading}>
                        <Row>
                            <div className="is-size-6 has-text-ldark-grey">Organization</div>
                            {/* <LBtn variation="unstyled">
                                <FontAwesomeIcon className="px-1" icon={solid("pen-to-square")} />
                                Edit organization
                            </LBtn> */}
                        </Row>
                        <LDivider isFat />
                    </SkeletonWrapper>
                    <SkeletonWrapper className="mb-5" loading={loading}>
                        <Row>
                            <Tile title="Account Name" content={organization?.orgName} isBoldContent />
                            <Tile title="Country" content={organization?.address.country} isBoldContent />
                        </Row>
                    </SkeletonWrapper>
                    <SkeletonWrapper className="mb-6" loading={loading}>
                        <Tile title="Additional Details" content={organization?.details} containerClasses="mt-4 mb-6" />
                    </SkeletonWrapper>
                    <SkeletonWrapper className="mb-5" loading={loading}>
                        <Row>
                            <div className="is-size-5 has-text-grey">Primary Contact</div>
                            <LBtn variation="unstyled" onClick={handleEditPrimaryContactClick}>
                                <FontAwesomeIcon className="px-1" icon={solid("pen-to-square")} />
                                Edit Primary Contact
                            </LBtn>
                            {isPrimaryContactModalOpen && (
                                <EditPrimaryContactModal
                                    organization={organization!}
                                    fullHeight={false}
                                    onClose={handleEditPrimaryContactClick}
                                    onSubmit={handleSubmitEditPrimaryContact}
                                />
                            )}
                        </Row>
                        <LDivider />
                    </SkeletonWrapper>
                    <SkeletonWrapper className="mb-5" loading={loading}>
                        <Row>
                            <Tile
                                title="Primary Contact Name"
                                content={[
                                    organization?.primaryContact?.firstName,
                                    organization?.primaryContact?.lastName
                                ].join(" ")}
                                isBoldContent
                            />
                            <Tile title="Job Title" content={organization?.primaryContact?.jobTitle} isBoldContent />
                        </Row>
                    </SkeletonWrapper>
                    <SkeletonWrapper className="mb-6" loading={loading}>
                        <Row marginBottom={6}>
                            <Tile
                                title="Primary Email"
                                content={organization?.primaryContact?.email}
                                containerClasses="mt-4"
                                isBoldContent
                            />
                            <Tile
                                title="Phone Number"
                                content={organization?.primaryContact?.phoneNumber}
                                containerClasses="mt-4"
                                isBoldContent
                            />
                        </Row>
                    </SkeletonWrapper>
                    {isActiveOrganization && (
                        <>
                            <div>
                                <Row>
                                    <SkeletonWrapper loading={loading}>
                                        <div className="is-size-5 has-text-grey">LASSI-D™ Assessments</div>
                                        <LBtn
                                            backgroundColor="#F2F7FA"
                                            variation="unstyled"
                                            disabled={showAssessmentQuantityForm}
                                            onClick={() => setShowAssessmentQuantityForm(true)}
                                        >
                                            + Add Assessments
                                        </LBtn>
                                    </SkeletonWrapper>
                                </Row>
                            </div>
                            <LDivider />
                            <Row>
                                <MultiLineTile
                                    title="Assessments Purchased"
                                    items={purchaseHistory.map((p) => ({
                                        value: p.quantity,
                                        tooltipText: new Date(p.purchaseDate + "Z").toString()
                                    }))}
                                    loading={loading}
                                />
                                <MultiLineTile
                                    title="Assessments Used"
                                    items={purchaseHistory.map((p) => ({
                                        value: p.assessmentsUsed,
                                        tooltipText: new Date(p.purchaseDate + "Z").toString()
                                    }))}
                                    loading={loading}
                                />
                                <MultiLineTile
                                    title="Utilization (%)"
                                    items={purchaseHistory.map((p) => ({
                                        value:
                                            (p.assessmentsUsed && (p.assessmentsUsed / p.quantity) * 100).toFixed(0) +
                                            "%",
                                        tooltipText: new Date(p.purchaseDate + "Z").toString()
                                    }))}
                                    loading={loading}
                                />
                                <MultiLineTile
                                    title="Unit Price"
                                    items={purchaseHistory.map((p) => ({
                                        value: `$${p.unitPrice}`,
                                        tooltipText: new Date(p.purchaseDate + "Z").toString()
                                    }))}
                                    loading={loading}
                                />
                                <MultiLineTile
                                    title="Discount"
                                    items={purchaseHistory.map((p) => ({
                                        value: p.discount ? `${p.discount}%` : "N/A",
                                        tooltipText: new Date(p.purchaseDate + "Z").toString()
                                    }))}
                                    loading={loading}
                                />
                                {showAssessmentQuantityForm && <LDivider />}
                            </Row>
                        </>
                    )}
                    {showAssessmentQuantityForm ? (
                        <div className="mb-3">
                            <div className="my-5 mb-6">
                                <OrganizationRequestAssessmentQuantity
                                    control={validateMethods.control}
                                    loading={loading}
                                />
                            </div>
                        </div>
                    ) : (
                        !isActiveOrganization && (
                            <>
                                <SkeletonWrapper className="mb-3" loading={loading}>
                                    <LDivider isFat />
                                    <div className="has-text-weight-bold is-5 mb-5">
                                        To grant this organization access, add assessments and specify an administrator
                                    </div>
                                </SkeletonWrapper>
                            </>
                        )
                    )}
                    {isActiveOrganization && (
                        <>
                            <SkeletonWrapper className="mb-5" loading={loading}>
                                <Row marginTop={3}>
                                    <div className="is-size-5 has-text-grey mt-5">Organization Administrator</div>
                                    {/* <LBtn variation="unstyled">
                                        <FontAwesomeIcon className="px-1" icon={solid("pen-to-square")} />
                                        Edit Administrator
                                    </LBtn> */}
                                </Row>
                                <LDivider />
                                This person will be responsible for adding and configuring staff and locations for this
                                organization or assigning someone else at their organization
                            </SkeletonWrapper>
                            <SkeletonWrapper className="mb-5" loading={loading}>
                                <Row marginTop={3}>
                                    <Tile
                                        title="Administrator Name"
                                        content={[
                                            organization?.administrator?.firstName,
                                            organization?.administrator?.lastName
                                        ].join(" ")}
                                        isBoldContent
                                    />
                                    <Tile
                                        title="Job Title"
                                        content={organization?.administrator?.jobTitle}
                                        isBoldContent
                                    />
                                </Row>
                            </SkeletonWrapper>
                            <SkeletonWrapper className="mb-6" loading={loading}>
                                <Row marginBottom={6}>
                                    <Tile
                                        title="Administrator Email"
                                        content={organization?.administrator?.email}
                                        containerClasses="mt-4"
                                        isBoldContent
                                    />
                                    <Tile
                                        title="Phone Number"
                                        content={organization?.administrator?.phoneNumber}
                                        containerClasses="mt-4"
                                        isBoldContent
                                    />
                                </Row>
                            </SkeletonWrapper>
                        </>
                    )}
                    {isActiveOrganization && showAssessmentQuantityForm && (
                        <LBtn loading={loading} onClick={handleAddAssessments}>
                            Add Assessments
                        </LBtn>
                    )}
                    {!isActiveOrganization && (
                        <LBtn loading={loading} onClick={handleGrantAccess}>
                            Grant Access
                        </LBtn>
                    )}
                    {showAssessmentQuantityForm && (
                        <LBtn variation="unstyled" classes="has-text-weight-bold" onClick={handleCancel}>
                            Cancel
                        </LBtn>
                    )}
                </form>
            </FormProvider>
        </div>
    );
};

export default SuperOrganizationDetails;

interface AssessmentQuantityForm {
    assessmentQuantity: number;
    unitPrice: number;
    discount?: number;
}

interface MultiLineTileItem {
    value: string | number | undefined;
    tooltipText?: string;
}
